import { DateTime } from "luxon";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype;

const sameEntries = (previous: unknown[], next: unknown[]): boolean => previous.length === next.length && next.every((item, index) => item === previous[index]);

/**
 * `next`, reusing every part of `previous` that is equal to it, so a refetch that brings nothing new hands back the
 * same objects and nothing reading them recomputes. React Query's own sharing does this for plain JSON only; our
 * responses hold Luxon dates (the network layer converts them), which it would always treat as new.
 */
export const shareEqualDeep = (previous: unknown, next: unknown): unknown => {
	if (previous === next) return previous;
	if (DateTime.isDateTime(previous) && DateTime.isDateTime(next)) return previous.equals(next) ? previous : next;
	if (Array.isArray(previous) && Array.isArray(next)) {
		const shared = next.map((item, index) => shareEqualDeep(previous[index], item));
		return sameEntries(previous, shared) ? previous : shared;
	}
	if (isPlainObject(previous) && isPlainObject(next)) {
		const keys = Object.keys(next);
		const shared = Object.fromEntries(keys.map((key) => [key, shareEqualDeep(previous[key], next[key])]));
		const isUnchanged = Object.keys(previous).length === keys.length && keys.every((key) => key in previous && shared[key] === previous[key]);
		return isUnchanged ? previous : shared;
	}
	return next;
};
